import React, { useState } from "react";
import classes from "./LoginModal.module.css";
import { Col, Row } from "react-bootstrap";
import { Input } from "../../Component/Input/Input";
import { Button } from "../../Component/Button/Button";
import { toast } from "react-toastify";
import { apiHeader, BaseURL, imageUrl } from "../../config/apiUrl";
import { Post } from "../../Axios/AxiosFunctions";
import { useDispatch, useSelector } from "react-redux";
import { saveLoginUserData } from "../../store/auth/authSlice";
import ModalSkeleton from "../ModalSkeleton";
import validator from "validator";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { useLogo } from "../../contexts/LogoContext";

const LoginModal = ({ show, setShow, showSignUp }) => {
  const { logo } = useLogo();
  const fcmToken = useSelector((state) => state?.authReducer?.fcmToken);

  // Tab state
  const [activeTab, setActiveTab] = useState("email"); // "password" or "email"

  // Password tab states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Email tab states
  const [emailForCode, setEmailForCode] = useState("");
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [codeLoading, setCodeLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Forget password modal
  const [forgetPasswordModal, setForgetPasswordModal] = useState(false);
  const [forgetLoading, setForgetLoading] = useState(false);

  const dispatch = useDispatch();
  const headers = apiHeader();

  const handleLogin = async () => {
    let params = {
      email,
      password,
    };
    if (params["password"]?.length < 8) {
      return toast.error("Password must contain min 8 characters");
    }

    if (!validator.isEmail(params["email"])) {
      return toast.warn("Your Email is not valid.");
    }

    for (let key in params) {
      if (params[key] === "") {
        return toast.error("Please fill all the required fields!");
      }
    }
    params = {
      ...params,
      fcmToken,
    };
    const url = BaseURL("auth/login");
    setLoading(true);
    const response = await Post(url, params, headers);
    if (response !== undefined) {
      localStorage.setItem("lastActivityTime", new Date().getTime());
      await dispatch(saveLoginUserData(response?.data));
      setShow(false);
      toast.success("Successfully Login");
    }
    setLoading(false);
  };

  // Send verification code handler
  const handleSendCode = async () => {
    if (!validator.isEmail(emailForCode)) {
      return toast.warn("Your Email is not valid.");
    }

    if (emailForCode === "" || emailForCode == null) {
      return toast.error("Please enter your email address!");
    }

    const params = {
      email: emailForCode,
    };

    // Adjust the URL endpoint as needed for your API
    const url = BaseURL("auth/sendVerificationCode");
    setCodeLoading(true);
    const response = await Post(url, params, headers);
    if (response !== undefined) {
      toast.success("Verification code sent to your email");
    }
    setCodeLoading(false);
  };

  // Email + code login handler
  const handleEmailLogin = async () => {
    if (!validator.isEmail(emailForCode)) {
      return toast.warn("Your Email is not valid.");
    }

    const codeString = verificationCode.join("");
    if (emailForCode === "" || codeString === "") {
      return toast.error("Please fill all the required fields!");
    }

    if (codeString.length < 6) {
      return toast.error("Please enter a valid 6-digit verification code");
    }

    const params = {
      email: emailForCode,
      verificationCode: codeString,
      fcmToken,
    };

    // Adjust the URL endpoint as needed for your API
    const url = BaseURL("auth/loginWithCode");
    setEmailLoading(true);
    const response = await Post(url, params, headers);
    if (response !== undefined) {
      localStorage.setItem("lastActivityTime", new Date().getTime());
      await dispatch(saveLoginUserData(response?.data));
      setShow(false);
      toast.success("Successfully Login");
    }
    setEmailLoading(false);
  };

  // Forget password handler
  const handleForget = async () => {
    const params = {
      email,
    };

    if (!validator.isEmail(params["email"])) {
      return toast.warn("Your Email is not valid.");
    }

    if (params["email"] === "" || params["email"] == null) {
      return toast.error("Please fill all the email field!");
    }
    const url = BaseURL("auth/forgotPassword");
    setForgetLoading(true);
    const response = await Post(url, params, headers);
    if (response !== undefined) {
      toast.success("Successfully Submitted.Please Check Your Email");
      setForgetPasswordModal(false);
    }
    setForgetLoading(false);
  };

  // Reset states when switching tabs
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    // Reset form states
    setEmail("");
    setPassword("");
    setEmailForCode("");
    setVerificationCode(["", "", "", "", "", ""]);
  };

  // Handle verification code input
  const handleCodeChange = (index, value) => {
    // Allow only alphanumeric characters
    const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '');

    if (sanitizedValue.length <= 1) {
      const newCode = [...verificationCode];
      newCode[index] = sanitizedValue.toUpperCase();
      setVerificationCode(newCode);

      // Auto-focus next input
      if (sanitizedValue && index < 5) {
        const nextInput = document.getElementById(`code-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // Handle backspace in code inputs
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Handle paste in code inputs
  const handleCodePaste = (index, e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    if (pastedData.length >= 6) {
      const newCode = [...verificationCode];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pastedData[i] || '';
      }
      setVerificationCode(newCode);

      // Focus the last filled input or the 6th input
      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      const targetInput = document.getElementById(`code-input-${lastFilledIndex}`);
      if (targetInput) targetInput.focus();
    } else if (pastedData.length > 0) {
      // For shorter pastes, fill from current position
      const newCode = [...verificationCode];
      for (let i = 0; i < pastedData.length && (index + i) < 6; i++) {
        newCode[index + i] = pastedData[i];
      }
      setVerificationCode(newCode);

      // Focus next empty input or last filled
      const nextFocusIndex = Math.min(index + pastedData.length, 5);
      const targetInput = document.getElementById(`code-input-${nextFocusIndex}`);
      if (targetInput) targetInput.focus();
    }
  };

  return (
    <>
      <style>{`
        .modal-content{
          border-radius:16px !important;
        }
        .tab-container {
          display: flex;
          border-bottom: 1px solid #e0e0e0;
          margin-bottom: 20px;
        }
        .tab-button {
          flex: 1;
          padding: 12px 20px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #666;
          border-bottom: 2px solid transparent;
          transition: all 0.3s ease;
        }
        .tab-button.active {
          color: var(--main-color);
          border-bottom-color: var(--main-color);
        }
        .tab-button:hover {
          color: var(--main-color);
        }
        .code-inputs-container {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin: 15px 0;
        }
        .code-input {
          width: 45px;
          height: 45px;
          text-align: center;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 18px;
          font-weight: bold;
          background: #fff;
          transition: all 0.3s ease;
        }
        .code-input:focus {
          outline: none;
          border-color: var(--main-color);
          box-shadow: 0 0 0 2px rgba(var(--main-color-rgb), 0.2);
        }
        .code-input:hover {
          border-color: var(--main-color);
        }
      `}</style>
      <ModalSkeleton
        show={show}
        setShow={setShow}
        modalClass={[classes.loginModal]}
        width={"752px"}
      >
        {forgetPasswordModal ? (
          <Row>
            <Col md={12}>
              <div className={`modal_header_logo`}>
                <img src={`${imageUrl}${logo}`} alt="Logo" width={150} />
              </div>
            </Col>

            <Col md={12} className={classes.fogetPassHeaderCol}>
              <MdOutlineArrowBackIosNew
                color={"var(--main-color)"}
                size={26}
                className={classes.backIcon}
                onClick={() => {
                  setEmail("");
                  setForgetPasswordModal(false);
                }}
              />
              <div className={classes.loginText}>
                <h3>Forget Password</h3>
              </div>
            </Col>
            <Col md={12}>
              <div className={[classes.inputCol]}>
                <Input
                  placeholder="Email"
                  type="email"
                  onEnterClick={handleForget}
                  value={email}
                  setter={setEmail}
                  label={"Email"}
                />
              </div>
            </Col>
            <Col md={12} className={[classes.btnCol]}>
              <Button
                label={forgetLoading ? "Submitting..." : "Submit"}
                onClick={handleForget}
                disabled={forgetLoading}
              />
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={12}>
              <div className={`modal_header_logo`} style={{ justifyContent: 'left', marginBottom: 0 }}>
                <img src={`${imageUrl}${logo}`} alt="Logo" width={150} />
              </div>
            </Col>

            <Col md={12}>
              <div className={classes.loginText}>
                <h3>SIGN IN</h3>
              </div>
            </Col>

            {/* Tab Navigation */}
            <Col md={12}>
              <div className="tab-container">
                <button
                  className={`tab-button ${activeTab === "email" ? "active" : ""}`}
                  onClick={() => handleTabSwitch("email")}
                >
                  GET CODE
                </button>
                <button
                  className={`tab-button ${activeTab === "password" ? "active" : ""}`}
                  onClick={() => handleTabSwitch("password")}
                >
                  USE PASSWORD
                </button>
              </div>
            </Col>

            {/* Password Tab Content */}
            {activeTab === "password" && (
              <Col md={12}>
                <div className={[classes.inputCol]}>
                  <Input
                    placeholder="Email"
                    type="email"
                    value={email}
                    setter={setEmail}
                    label={"Email"}
                    onEnterClick={handleLogin}
                    autocomplete={"off"}
                  />
                </div>
                <div className={[classes.inputCol]}>
                  <Input
                    placeholder="Password"
                    value={password}
                    setter={setPassword}
                    type="password"
                    label={"Password"}
                    onEnterClick={handleLogin}
                    autocomplete={"off"}
                  />
                </div>

                <div className={[classes.inputCol]}>
                  <p className={classes.link} onClick={() => {
                    setEmail("");
                    setForgetPasswordModal(true);
                  }}>
                    Forgot Password?
                  </p>
                </div>
                <Button
                  className={classes.loginBtnCode}
                  label={loading ? "submitting..." : "SIGN IN"}
                  onClick={handleLogin}
                  disabled={loading}
                />
                <div
                  onClick={() => {
                    setShow(false);
                    showSignUp(true);
                  }}
                  className={[classes.SignUpDiv]}
                >
                  Don’t have an account set up?&nbsp;
                  <span className={[classes.SignUpText]}>Create account</span>
                </div>
              </Col>
            )}

            {/* Email Code Tab Content */}
            {activeTab === "email" && (
              <Col md={12}>
                <div className={[classes.inputCol]}>
                  <Input
                    placeholder="Email"
                    type="email"
                    value={emailForCode}
                    setter={setEmailForCode}
                    label={"Email"}
                    autocomplete={"off"}
                  />
                  <Button
                    label={codeLoading ? "Sending..." : "Send Code"}
                    onClick={handleSendCode}
                    disabled={codeLoading}
                    style={{ marginTop: "10px", width: "100%" }}
                  />
                </div>
                <div className={[classes.inputCol]}>
                  <Input
                    placeholder="Verification Code"
                    value=""
                    setter={() => { }}
                    type="text"
                    label={"Verification Code"}
                    style={{ display: 'none' }} // Hide the original input
                  />
                  <div className="code-inputs-container">
                    {verificationCode.map((digit, index) => (
                      <input
                        key={index}
                        id={`code-input-${index}`}
                        type="text"
                        className="code-input"
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        onPaste={(e) => handleCodePaste(index, e)}
                        maxLength={1}
                        autoComplete="off"
                      />
                    ))}
                  </div>
                </div>
                <Button
                  className={classes.loginBtn}
                  label={emailLoading ? "submitting..." : "SIGN IN"}
                  onClick={handleEmailLogin}
                  disabled={emailLoading}
                />
                <div
                  onClick={() => {
                    setShow(false);
                    showSignUp(true);
                  }}
                  className={[classes.SignUpDiv]}
                >
                  Don’t have an account set up?&nbsp;
                  <span className={[classes.SignUpText]}>Create account</span>
                </div>
              </Col>
            )}
          </Row>
        )}
      </ModalSkeleton>
    </>
  );
};

export default LoginModal;
